import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, map } from 'rxjs';

import { environment } from '../../../enviroment';
import { ApiResponse, PagedResult, PaginationParams } from '../models/api.models';

@Injectable({ providedIn: 'root' })
export class ApiService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = environment.apiUrl;

  get<T>(path: string, params?: PaginationParams): Observable<T> {
    return this.http
      .get<ApiResponse<T>>(this.url(path), { params: this.toParams(params) })
      .pipe(map((res) => this.unwrap(res)));
  }

  getPaged<T>(path: string, params?: PaginationParams): Observable<PagedResult<T>> {
    return this.http
      .get<ApiResponse<unknown>>(this.url(path), { params: this.toParams(params) })
      .pipe(map((res) => this.normalizePaged<T>(this.unwrap(res), params)));
  }

  post<T>(path: string, body?: unknown): Observable<T> {
    return this.http
      .post<ApiResponse<T>>(this.url(path), body ?? {})
      .pipe(map((res) => this.unwrap(res)));
  }

  put<T>(path: string, body?: unknown): Observable<T> {
    return this.http
      .put<ApiResponse<T>>(this.url(path), body ?? {})
      .pipe(map((res) => this.unwrap(res)));
  }

  patch<T>(path: string, body?: unknown): Observable<T> {
    return this.http
      .patch<ApiResponse<T>>(this.url(path), body ?? {})
      .pipe(map((res) => this.unwrap(res)));
  }

  delete<T>(path: string): Observable<T> {
    return this.http
      .delete<ApiResponse<T>>(this.url(path))
      .pipe(map((res) => this.unwrap(res)));
  }

  /** Raw response without unwrapping — for legacy callers */
  getRaw<T = unknown>(path: string, params?: PaginationParams): Observable<ApiResponse<T> | T> {
    return this.http.get<ApiResponse<T> | T>(this.url(path), { params: this.toParams(params) });
  }

  /** Download binary export (CSV, etc.) — caller handles blob save. */
  downloadBlob(path: string, params?: Record<string, string | number>): Observable<Blob> {
    return this.http.get(this.url(path), {
      params: this.toParams(params),
      responseType: 'blob'
    });
  }

  private url(path: string): string {
    const cleaned = path.replace(/^\//, '');
    return `${this.baseUrl}/${cleaned}`;
  }

  private toParams(params?: PaginationParams): HttpParams | undefined {
    if (!params) return undefined;
    let httpParams = new HttpParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value === undefined || value === null || value === '') return;
      httpParams = httpParams.set(key, String(value));
    });
    return httpParams;
  }

  private unwrap<T>(res: ApiResponse<T>): T {
    if (res && typeof res === 'object' && 'success' in res) {
      if (!res.success) {
        throw new Error(res.message || 'Request failed.');
      }
      return res.data as T;
    }
    return res as unknown as T;
  }

  private normalizePaged<T>(data: unknown, params?: PaginationParams): PagedResult<T> {
    if (Array.isArray(data)) {
      return {
        items: data as T[],
        totalCount: data.length,
        page: params?.page ?? 1,
        pageSize: params?.pageSize ?? data.length,
        totalPages: 1
      };
    }

    const obj = (data ?? {}) as Record<string, unknown>;
    const items = (obj['items'] ?? obj['Items'] ?? []) as T[];
    const totalCount = Number(obj['totalCount'] ?? obj['TotalCount'] ?? items.length);
    const page = Number(obj['page'] ?? obj['Page'] ?? params?.page ?? 1);
    const pageSize = Number(obj['pageSize'] ?? obj['PageSize'] ?? params?.pageSize ?? 20);
    const totalPages = Number(
      obj['totalPages'] ?? obj['TotalPages'] ?? Math.ceil(totalCount / Math.max(pageSize, 1))
    );

    return { items, totalCount, page, pageSize, totalPages };
  }
}
